import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Users,
  Search,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Phone,
  Globe,
  Bot,
  MessageSquare,
} from "lucide-react";
import {
  getHelpdeskContacts,
  updateHelpdeskContact,
  refreshContactLookup,
} from "../../../../api/helpdeskContacts";
import { apiBodyMessage } from "../../../../utils/apiErrorMessage";
import CommerceBadge from "../../../../components/helpdesk/CommerceBadge";
import CommerceSelector from "../../../../components/helpdesk/CommerceSelector";
import { showToast } from "../../../../utils/showToast";
import HelpdeskSuiteLayout from "../../../../layouts/helpdeskLayout/HelpdeskSuiteLayout";
import { cn } from "../../../../utils/cn";
import { PageHeader } from "../../../../components/ui/PageHeader";
import { useIsMobile } from "../../../../hooks/useIsMobile";

// Campos derivados de un contacto, compartidos entre la tabla desktop y las
// cards mobile (evita duplicar la lógica de display name/alias/seed).
function deriveContact(contact) {
  const displayName =
    (contact.alias && contact.alias.trim()) ||
    contact.display_name ||
    contact.external_id ||
    "—";
  const showOriginal =
    contact.alias &&
    contact.display_name &&
    contact.alias.trim() !== contact.display_name;
  const initial = (displayName?.trim()?.[0] || "?").toUpperCase();
  const seed = contact.id || contact.external_id || contact.phone || displayName;
  return { displayName, showOriginal, initial, seed };
}

// Texto visible de un error de la API de contactos: los wrappers devuelven
// `{ error }` con el mensaje del backend (string de Nest, array del
// ValidationPipe u objeto), o el literal "Error desconocido" cuando no hubo
// respuesta. Ese literal no es texto para el usuario: ahí va el fallback
// traducido, y cuando el cuerpo no trae nada útil también.
function contactErrorMessage(error, fallback) {
  if (error === "Error desconocido") return fallback;
  return apiBodyMessage(error) ?? fallback;
}

// Hash → tono determinístico para el avatar del contacto (mismo set que la
// bandeja, para reforzar reconocimiento de un mismo cliente entre vistas).
function avatarTone(seed) {
  const tones = [
    "bg-brand-100 text-brand-700",
    "bg-status-info-soft text-status-info",
    "bg-status-success-soft text-status-success",
    "bg-status-warning-soft text-status-warning",
    "bg-status-cc-soft text-status-cc",
  ];
  if (!seed) return tones[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return tones[h % tones.length];
}

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", className: "text-[#128C7E]", Icon: MessageSquare },
  whatsapp_unofficial: { label: "WhatsApp", className: "text-[#128C7E]", Icon: MessageSquare },
  messenger: { label: "Messenger", className: "text-[#0084FF]", Icon: MessageSquare },
  instagram: { label: "Instagram", className: "text-[#E4405F]", Icon: MessageSquare },
  portal: { label: "Portal", className: "text-fg-muted", Icon: Globe },
  ai: { label: "IA", className: "text-brand-600", Icon: Bot },
};

function ContactChannelTag({ channel }) {
  const meta = CHANNEL_META[channel] || {
    label: channel || "—",
    className: "text-fg-muted",
    Icon: MessageSquare,
  };
  const Icon = meta.Icon;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
      <Icon className={cn("w-3 h-3 shrink-0", meta.className)} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

export default function AdminContacts() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [commerceFilter, setCommerceFilter] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [pageSize] = useState(50);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await getHelpdeskContacts({
      q: search || undefined,
      commerce_id: commerceFilter?.id || undefined,
      limit: pageSize,
      offset,
    });
    if (result?.error) {
      showToast(t("No se pudieron cargar los contactos"), false);
    } else {
      setContacts(result?.contacts || []);
      setTotal(result?.count || 0);
    }
    setLoading(false);
  }, [search, commerceFilter, pageSize, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setOffset(0);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const handleSetCommerce = async (contact, commerce) => {
    const result = await updateHelpdeskContact(contact.id, {
      default_commerce_id: commerce?.id || null,
    });
    if (result?.error) {
      showToast(
        contactErrorMessage(result.error, t("No se pudo actualizar el contacto")),
        false,
      );
      return;
    }
    setContacts((prev) =>
      prev.map((c) => (c.id === contact.id ? { ...c, ...result.contact } : c)),
    );
    showToast(t("Comercio actualizado"), true);
    setEditingId(null);
  };

  const handleRefresh = async (contact) => {
    setRefreshingId(contact.id);
    const result = await refreshContactLookup(contact.id);
    setRefreshingId(null);
    if (result?.error) {
      showToast(contactErrorMessage(result.error, t("Error al refrescar")), false);
      return;
    }
    setContacts((prev) =>
      prev.map((c) => (c.id === contact.id ? { ...c, ...result.contact } : c)),
    );
    if (result.contact?.matched) {
      showToast(t("Comercio encontrado"), true);
    } else {
      showToast(t("No se encontró comercio"), false);
    }
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setCommerceFilter(null);
    setOffset(0);
  };

  const hasFilters = !!searchInput || !!commerceFilter;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + pageSize, total);

  return (
    <HelpdeskSuiteLayout activeKey="suite.contacts">
      <div className="flex flex-col h-full min-h-0 bg-bg">
        {/* ── Contenido scrolleable ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-5 py-5">

            {/* ── Header de página ──────────────────────────────── */}
            <PageHeader
              icon={
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
                  <Users className="w-5 h-5" aria-hidden="true" />
                </span>
              }
              title={t("Contactos")}
              subtitle={t("Personas y números con conversaciones en tu mesa de ayuda.")}
              className="mb-5"
            />

            {/* Toolbar */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              {/* Buscador */}
              <div className="w-full md:flex-1 md:w-auto md:min-w-[240px] md:max-w-md">
                <label
                  htmlFor="contacts-search"
                  className="hidden md:block text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1"
                >
                  {t("Buscar")}
                </label>
                <div className="relative">
                  <Search
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-subtle pointer-events-none"
                    aria-hidden="true"
                  />
                  <input
                    id="contacts-search"
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t("Nombre o teléfono…")}
                    className="w-full h-11 md:h-9 pl-8 pr-8 rounded-md border border-border bg-bg text-base md:text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => setSearchInput("")}
                      aria-label={t("Limpiar búsqueda")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex w-5 h-5 items-center justify-center rounded text-fg-subtle hover:bg-bg-muted hover:text-fg"
                    >
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              {/* Filtro comercio */}
              <div className="w-full md:w-[240px] min-w-0">
                <label className="hidden md:block text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">
                  {t("Comercio")}
                </label>
                <CommerceSelector
                  value={commerceFilter}
                  onChange={(c) => {
                    setCommerceFilter(c);
                    setOffset(0);
                  }}
                  placeholder={t("Todos")}
                  size="sm"
                />
              </div>

              {/* Limpiar + total */}
              <div className="flex items-center gap-3 ml-auto">
                {hasFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md text-xs text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
                  >
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                    {t("Limpiar")}
                  </button>
                )}
                <div className="text-xs text-fg-muted whitespace-nowrap">
                  {loading
                    ? t("Cargando…")
                    : total === 0
                    ? t("Sin resultados")
                    : `${showingFrom}–${showingTo} ${t("de")} ${total}`}
                </div>
              </div>
            </div>

            {/* ── Mobile: cards (una por contacto) ─────────────────── */}
            {isMobile ? (
              <div className="flex flex-col gap-2.5">
                {loading ? (
                  <ContactsCardsSkeleton rows={6} />
                ) : contacts.length === 0 ? (
                  <div className="rounded-lg border border-border bg-bg">
                    <ContactsEmpty
                      hasFilters={hasFilters}
                      onClear={handleClearFilters}
                      t={t}
                    />
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      isEditing={editingId === contact.id}
                      isRefreshing={refreshingId === contact.id}
                      onEdit={() => setEditingId(contact.id)}
                      onSetCommerce={handleSetCommerce}
                      onRefresh={handleRefresh}
                      t={t}
                    />
                  ))
                )}
              </div>
            ) : (
              /* ── Desktop: tabla en grid ───────────────────────────── */
              <div className="rounded-lg border border-border bg-bg overflow-x-auto">
                {/* Cabecera */}
                <div className="grid min-w-[840px] grid-cols-[minmax(220px,2fr)_minmax(140px,1fr)_120px_minmax(180px,1fr)_120px_56px] gap-3 px-4 h-10 items-center text-[10px] uppercase tracking-wider font-semibold text-fg-subtle bg-bg-muted/50 border-b border-border">
                  <span>{t("Contacto")}</span>
                  <span>{t("Teléfono")}</span>
                  <span>{t("Canal")}</span>
                  <span>{t("Comercio")}</span>
                  <span>{t("Última actividad")}</span>
                  <span className="text-right pr-1">{t("Acciones")}</span>
                </div>

                {/* Rows */}
                {loading ? (
                  <ContactsSkeleton rows={6} />
                ) : contacts.length === 0 ? (
                  <ContactsEmpty hasFilters={hasFilters} onClear={handleClearFilters} t={t} />
                ) : (
                  contacts.map((contact) => {
                    const isEditing = editingId === contact.id;
                    const isRefreshing = refreshingId === contact.id;
                    const { displayName, showOriginal, initial, seed } =
                      deriveContact(contact);

                    return (
                      <div
                        key={contact.id}
                        className={cn(
                          "grid min-w-[840px] grid-cols-[minmax(220px,2fr)_minmax(140px,1fr)_120px_minmax(180px,1fr)_120px_56px] gap-3 px-4 py-2.5 items-center border-b border-border/60 last:border-b-0 transition-colors",
                          isEditing ? "bg-brand-50/40" : "hover:bg-bg-muted/40",
                        )}
                      >
                        {/* Contacto */}
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0",
                              avatarTone(seed),
                            )}
                            aria-hidden="true"
                          >
                            {initial}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-fg font-medium truncate" title={displayName}>
                              {displayName}
                            </div>
                            {showOriginal && (
                              <div className="text-[12px] md:text-[11px] text-fg-subtle truncate">
                                {contact.display_name}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Teléfono */}
                        <div className="text-xs text-fg-muted truncate flex items-center gap-1" title={contact.phone}>
                          <Phone className="w-3 h-3 text-fg-subtle shrink-0" aria-hidden="true" />
                          <span className="truncate">{contact.phone || "—"}</span>
                        </div>

                        {/* Canal */}
                        <div className="min-w-0">
                          <ContactChannelTag channel={contact.channel} />
                        </div>

                        {/* Comercio */}
                        <div className="min-w-0">
                          {isEditing ? (
                            <CommerceSelector
                              value={
                                contact.default_commerce_id
                                  ? {
                                      id: contact.default_commerce_id,
                                      name: contact.default_commerce_name || "",
                                    }
                                  : null
                              }
                              onChange={(c) => handleSetCommerce(contact, c)}
                              placeholder={t("Buscar comercio…")}
                              size="sm"
                              autoFocus
                            />
                          ) : (
                            <CommerceBadge
                              commerceId={contact.default_commerce_id}
                              commerceName={contact.default_commerce_name}
                              emptyLabel={t("Sin asignar")}
                              onClick={() => setEditingId(contact.id)}
                              size="sm"
                            />
                          )}
                        </div>

                        {/* Última actividad */}
                        <div className="text-xs text-fg-muted whitespace-nowrap" title={contact.last_seen_at || ""}>
                          {contact.last_seen_at ? dayjs(contact.last_seen_at).fromNow() : "—"}
                        </div>

                        {/* Acciones */}
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleRefresh(contact)}
                            disabled={isRefreshing}
                            title={t("Re-ejecutar búsqueda automática de comercio")}
                            aria-label={t("Re-ejecutar búsqueda automática de comercio")}
                            className="inline-flex w-7 h-7 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <RefreshCw
                              className={cn("w-3.5 h-3.5", isRefreshing && "animate-spin")}
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Paginación */}
            {total > pageSize && (
              <div className="mt-4 flex items-center justify-end gap-3">
                <span className="text-xs text-fg-muted">
                  {showingFrom}–{showingTo} {t("de")} {total}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={offset === 0 || loading}
                    onClick={() => setOffset(Math.max(0, offset - pageSize))}
                    aria-label={t("Página anterior")}
                    className="inline-flex w-8 h-8 items-center justify-center rounded-md border border-border bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    disabled={offset + pageSize >= total || loading}
                    onClick={() => setOffset(offset + pageSize)}
                    aria-label={t("Página siguiente")}
                    className="inline-flex w-8 h-8 items-center justify-center rounded-md border border-border bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </HelpdeskSuiteLayout>
  );
}

// ── Card de un contacto (mobile) — transforma la fila de la tabla en card ──
function ContactCard({
  contact,
  isEditing,
  isRefreshing,
  onEdit,
  onSetCommerce,
  onRefresh,
  t,
}) {
  const { displayName, showOriginal, initial, seed } = deriveContact(contact);

  return (
    <div
      className={cn(
        "rounded-lg border bg-bg p-3 flex flex-col gap-2.5 transition-colors",
        isEditing ? "border-brand-300 bg-brand-50/40" : "border-border",
      )}
    >
      {/* Cabecera: avatar + nombre + acción refrescar */}
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
            avatarTone(seed),
          )}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-fg font-medium truncate" title={displayName}>
            {displayName}
          </div>
          {showOriginal && (
            <div className="text-[12px] md:text-[11px] text-fg-subtle truncate">
              {contact.display_name}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRefresh(contact)}
          disabled={isRefreshing}
          title={t("Re-ejecutar búsqueda automática de comercio")}
          aria-label={t("Re-ejecutar búsqueda automática de comercio")}
          className="inline-flex w-8 h-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          <RefreshCw
            className={cn("w-4 h-4", isRefreshing && "animate-spin")}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Meta: teléfono · canal · última actividad */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="inline-flex items-center gap-1 text-xs text-fg-muted min-w-0">
          <Phone className="w-3 h-3 text-fg-subtle shrink-0" aria-hidden="true" />
          <span className="truncate">{contact.phone || "—"}</span>
        </span>
        <ContactChannelTag channel={contact.channel} />
        <span className="text-xs text-fg-muted whitespace-nowrap">
          {contact.last_seen_at ? dayjs(contact.last_seen_at).fromNow() : "—"}
        </span>
      </div>

      {/* Comercio */}
      <div className="pt-0.5">
        {isEditing ? (
          <CommerceSelector
            value={
              contact.default_commerce_id
                ? {
                    id: contact.default_commerce_id,
                    name: contact.default_commerce_name || "",
                  }
                : null
            }
            onChange={(c) => onSetCommerce(contact, c)}
            placeholder={t("Buscar comercio…")}
            size="sm"
            autoFocus
          />
        ) : (
          <CommerceBadge
            commerceId={contact.default_commerce_id}
            commerceName={contact.default_commerce_name}
            emptyLabel={t("Sin asignar")}
            onClick={onEdit}
            size="sm"
          />
        )}
      </div>
    </div>
  );
}

function ContactsCardsSkeleton({ rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-border bg-bg p-3 flex flex-col gap-2.5"
        >
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-full bg-bg-muted animate-pulse shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-32 bg-bg-muted rounded animate-pulse" />
              <div className="h-2.5 w-20 bg-bg-muted rounded animate-pulse" />
            </div>
            <div className="w-8 h-8 rounded-md bg-bg-muted animate-pulse shrink-0" />
          </div>
          <div className="flex items-center gap-4">
            <div className="h-3 w-24 bg-bg-muted rounded animate-pulse" />
            <div className="h-3 w-16 bg-bg-muted rounded animate-pulse" />
            <div className="h-3 w-12 bg-bg-muted rounded animate-pulse" />
          </div>
          <div className="h-6 w-36 bg-bg-muted rounded animate-pulse" />
        </div>
      ))}
    </>
  );
}

function ContactsSkeleton({ rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="grid min-w-[840px] grid-cols-[minmax(220px,2fr)_minmax(140px,1fr)_120px_minmax(180px,1fr)_120px_56px] gap-3 px-4 py-2.5 items-center border-b border-border/60 last:border-b-0"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-bg-muted animate-pulse" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-32 bg-bg-muted rounded animate-pulse" />
              <div className="h-2.5 w-20 bg-bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="h-3 w-28 bg-bg-muted rounded animate-pulse" />
          <div className="h-3 w-16 bg-bg-muted rounded animate-pulse" />
          <div className="h-6 w-36 bg-bg-muted rounded animate-pulse" />
          <div className="h-3 w-20 bg-bg-muted rounded animate-pulse" />
          <div className="ml-auto h-6 w-6 rounded bg-bg-muted animate-pulse" />
        </div>
      ))}
    </>
  );
}

function ContactsEmpty({ hasFilters, onClear, t }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-12 h-12 rounded-full bg-bg-muted text-fg-subtle flex items-center justify-center mb-3">
        <Users className="w-5 h-5" aria-hidden="true" />
      </div>
      <p className="text-sm font-medium text-fg">
        {hasFilters ? t("Sin resultados") : t("Aún no hay contactos")}
      </p>
      <p className="mt-1 max-w-sm text-xs text-fg-muted">
        {hasFilters
          ? t("Prueba ajustando los filtros o limpiándolos para ver todos los contactos.")
          : t("Cuando alguien escriba a tus canales conectados, aparecerá aquí su perfil de contacto.")}
      </p>
      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="mt-3 inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 transition-colors"
        >
          <X className="w-3 h-3" aria-hidden="true" />
          {t("Limpiar filtros")}
        </button>
      )}
    </div>
  );
}
